import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type mixpanel from "mixpanel-browser";
import { Outlet, useLocation } from "react-router-dom";
import { createEventContext, pageContext } from "./context";
import { storeExportImage, type ExportArtifact } from "./exportImage";

type Scalar = string | number | boolean;
type EventProperties = Readonly<Record<string, Scalar>>;
type MixpanelBrowser = typeof mixpanel;
export type AnalyticsEventName =
  | "page_viewed" | "landing_viewed" | "creator_cta_clicked" | "studio_opened"
  | "authentication_started" | "account_created" | "signed_in" | "signed_out"
  | "reference_added" | "reference_rejected" | "example_loaded" | "setup_completed"
  | "generation_started" | "generation_completed" | "generation_failed" | "generation_cancelled"
  | "download_opened" | "export_completed" | "pricing_viewed" | "plan_selected"
  | "checkout_started" | "checkout_opened" | "checkout_failed" | "subscription_activated";

const token = import.meta.env.VITE_MIXPANEL_TOKEN?.trim() ?? "";
const apiHost = import.meta.env.VITE_MIXPANEL_API_HOST?.trim() ?? "";
let mixpanelInstance: MixpanelBrowser | null = null;
let mixpanelLoad: Promise<MixpanelBrowser | null> | null = null;

function loadMixpanel(): Promise<MixpanelBrowser | null> {
  if (!token) return Promise.resolve(null);
  if (mixpanelInstance) return Promise.resolve(mixpanelInstance);
  mixpanelLoad ??= import("mixpanel-browser/src/loaders/loader-module-core")
    .then(module => {
      mixpanelInstance = (module as unknown as { default: MixpanelBrowser }).default;
      return mixpanelInstance;
    })
    .catch(() => null);
  return mixpanelLoad;
}

type AnalyticsContextValue = Readonly<{
  available: boolean;
  track: (event: AnalyticsEventName, properties?: EventProperties) => void;
  trackExport: (properties: EventProperties, artifact: ExportArtifact) => void;
  identify: (accountId: string) => void;
  reset: () => void;
}>;

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);
const unavailableAnalytics: AnalyticsContextValue = Object.freeze({
  available: false,
  track: () => undefined,
  trackExport: () => undefined,
  identify: () => undefined,
  reset: () => undefined,
});

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const initialized = useRef(false);
  const [eventContext] = useState(createEventContext);

  const initialize = useCallback(async () => {
    if (!token) return null;
    const mixpanel = await loadMixpanel();
    if (!mixpanel) return null;
    if (!initialized.current) {
      mixpanel.init(token, {
        ...(apiHost ? { api_host: apiHost } : {}),
        autocapture: false,
        track_pageview: false,
        persistence: "localStorage",
        property_blacklist: ["$referrer", "$initial_referrer", "$initial_referring_domain"],
        // Supported by SDK 2.83; absent from its bundled Config declaration.
        ...{ track_marketing: false },
        save_referrer: false,
        store_google: false,
        stop_utm_persistence: true,
        skip_first_touch_marketing: true,
        ip: true,
        secure_cookie: import.meta.env.PROD,
        opt_out_tracking_by_default: false,
        opt_out_persistence_by_default: false,
        ignore_dnt: false,
      });
      mixpanel.opt_in_tracking({ track: () => undefined });
      initialized.current = true;
    }
    // SDK-generated identity events also inherit a sanitized URL. App events
    // override this with the URL captured at the moment of the action.
    mixpanel.register({ $current_url: pageContext(new URL(window.location.href)).$current_url });
    return mixpanel;
  }, []);

  useEffect(() => { void initialize(); }, [initialize]);

  const value = useMemo<AnalyticsContextValue>(() => ({
    available: Boolean(token),
    track(event, properties = {}) {
      if (!token) return;
      const captured = { ...properties, ...eventContext.capture(), app_revision: import.meta.env.VITE_BUILD_REVISION ?? "development" };
      void initialize().then(mixpanel => {
        if (mixpanel) mixpanel.track(event, captured);
      });
    },
    trackExport(properties, artifact) {
      if (!token) return;
      // Capture the download's route/session now, even if upload finishes after navigation.
      const captured = { ...properties, ...eventContext.capture(), app_revision: import.meta.env.VITE_BUILD_REVISION ?? "development" };
      void Promise.all([initialize(), storeExportImage(artifact)]).then(([mixpanel, imageProperties]) => {
        if (mixpanel) mixpanel.track("export_completed", { ...captured, ...imageProperties });
      });
    },
    identify(accountId) {
      if (!token || !accountId) return;
      void initialize().then(mixpanel => { if (mixpanel) mixpanel.identify(accountId); });
    },
    reset() {
      if (!token) return;
      eventContext.clear();
      void initialize().then(mixpanel => { if (mixpanel) mixpanel.reset(); });
    },
  }), [initialize, eventContext]);

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

// The provider and hook intentionally share the private context.
// eslint-disable-next-line react-refresh/only-export-components
export function useAnalytics(): AnalyticsContextValue {
  return useContext(AnalyticsContext) ?? unavailableAnalytics;
}

export function AnalyticsRouteTracker() {
  const location = useLocation();
  const analytics = useAnalytics();
  const lastView = useRef<string | null>(null);
  const previousPath = useRef<string | null>(null);
  useEffect(() => {
    if (!analytics.available) { lastView.current = null; return; }
    const view = `${location.key}:${location.pathname}`;
    if (lastView.current === view) return;
    lastView.current = view;
    const context = pageContext(new URL(location.pathname, window.location.origin));
    const name = context.page_name;
    analytics.track("page_viewed", { previous_page_path: previousPath.current ?? "none" });
    previousPath.current = String(context.page_path);
    if (name === "landing") analytics.track("landing_viewed");
    if (name === "landing_experiment_lp_2") analytics.track("landing_viewed", { experiment: "lp-2" });
    if (name === "studio") analytics.track("studio_opened", { studio_variant: location.pathname });
  }, [analytics, location.key, location.pathname]);
  return <Outlet />;
}
